import type { HTMLAttributes, ReactNode } from 'react';

import { useOptionalT } from '@/lib/i18n';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  pad?: boolean;
  flat?: boolean;
}

export function Card({
  pad = false,
  flat = false,
  className = '',
  children,
  ...rest
}: CardProps) {
  return (
    <div
      className={[

        'rounded-[var(--radius-lg)] border border-line/70 bg-white',
        flat
          ? 'shadow-none'
          : 'shadow-[0_1px_2px_rgb(22_18_26/0.04),0_8px_24px_-12px_rgb(22_18_26/0.18)]',
        pad ? 'p-4' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

export type BadgeTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'ai' | 'pink';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-ink-100 text-ink-700',
  ok: 'bg-green-100 text-green-700',
  warn: 'bg-amber-100 text-amber-700',
  danger: 'bg-red-100 text-red-700',
  info: 'bg-blue-100 text-blue-700',

  ai: 'bg-ai-100 text-ai-700',
  pink: 'bg-pink-300 text-plum-800',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  children: ReactNode;
}

export function Badge({ tone = 'neutral', className = '', children, ...rest }: BadgeProps) {
  return (
    <span
      className={[
        'inline-flex items-center gap-[5px] rounded-[var(--radius-sm)] px-[9px] py-1',
        'text-[11px] font-semibold tracking-[0.01em] whitespace-nowrap',
        BADGE_TONES[tone],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </span>
  );
}

export interface ChipProps extends HTMLAttributes<HTMLElement> {
  onClick?: () => void;
  selected?: boolean;
  label?: string;
}

export function Chip({
  selected = false,
  onClick,
  label,
  className = '',
  children,
  ...rest
}: ChipProps) {
  const classes = [
    'inline-flex items-center gap-[6px] rounded-full border px-[14px] py-2',
    'text-[13px] font-semibold whitespace-nowrap',
    'transition-[background-color,color,border-color,transform]',
    'duration-[var(--duration-base)] ease-[var(--ease-brand)]',
    selected
      ? 'border-plum-700 bg-plum-700 text-white'
      : 'border-ink-200 bg-paper text-ink-700',
    onClick && !selected ? 'hover:bg-ink-50 hover:border-ink-300' : '',
    onClick ? 'cursor-pointer active:translate-y-px' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (onClick) {
    return (
      <button
        type="button"
        className={classes}
        onClick={onClick}
        aria-pressed={selected}
        aria-label={label}
        {...rest}
      >
        {children}
      </button>
    );
  }

  return (
    <span className={classes} {...rest}>
      {children}
    </span>
  );
}

export type PublicationStatus = 'published' | 'draft' | 'suspended';

const STATUS_MAP: Record<
  PublicationStatus,
  { tone: BadgeTone; labelKey: 'common.published' | 'common.draft' | 'common.suspended' }
> = {
  published: { tone: 'ok', labelKey: 'common.published' },
  draft: { tone: 'warn', labelKey: 'common.draft' },
  suspended: { tone: 'danger', labelKey: 'common.suspended' },
};

export function StatusBadge({ status }: { status: PublicationStatus }) {
  const t = useOptionalT();
  const { tone, labelKey } = STATUS_MAP[status];
  const label = t(labelKey);
  return (
    <Badge tone={tone}>
      <span aria-hidden="true">●</span>
      {label}
    </Badge>
  );
}